import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { DatabasePageParams } from '../../components/pages/DatabasePage/types';
import { MainCellProps, TextPreprocessor } from '../types';
import {
  handleTextFieldFirstSelect,
  handleTextFieldSecondSelect,
  isValidTextValue,
  shouldTextFieldExpand,
} from '../utils';

export const useTextField = (mainCell: MainCellProps, preprocessText?: TextPreprocessor) => {
  const {
    handleBlur,
    handleKeyDown,
    updateCellState,
    updateCellValue,
    invalid,
    state,
    value,
    dataType,
  } = mainCell;

  const [textValue, setTextValue] = useState('');
  const { databaseSlug, tableSlug } = useParams<DatabasePageParams>();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const initialTextAreaHeight = useRef(0);

  useEffect(() => {
    setTextValue(isValidTextValue(value) ? String(value) : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, invalid]);

  useEffect(() => {
    if (state === 'CellFirstSelect') {
      handleTextFieldFirstSelect(textAreaRef);
      initialTextAreaHeight.current = textAreaRef.current?.clientHeight || 0;
    } else if (state === 'CellSecondSelect') {
      // FIXME: expanded state is triggering too soon
      if (shouldTextFieldExpand(textAreaRef, initialTextAreaHeight.current)) {
        updateCellState?.('Expanded');
      }
      handleTextFieldSecondSelect(textAreaRef);
    }
  }, [state, updateCellState]);

  const onBlur = async () => {
    handleBlur?.();
    if (tableSlug) {
      await updateCellValue?.(databaseSlug, tableSlug, dataType === 'number' ? Number(textValue) : textValue);
    }
  };

  const onKeyDown = async (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    handleKeyDown?.(event);
    if (['Enter', 'Tab'].includes(event.key)) {
      const cellValue = (event?.target as HTMLTextAreaElement).value;
      if (tableSlug) {
        await updateCellValue?.(databaseSlug, tableSlug, dataType === 'number' ? Number(cellValue) : cellValue);
      }
    } 
    //FIXME: we need to revisit this logic as it is breaking SD-307
    //else if (state === 'CellFirstSelect' && !CHANGE_TEXT_EXCEPTION_KEYS.includes(event?.key)) {
    //   setTextValue('');
    // }
  };

  const handleTextChanged = (
    ref: React.RefObject<HTMLTextAreaElement>,
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    if (ref.current) {
      if (preprocessText) {
        preprocessText(ref, event);
      }

      const textAreaValue = ref.current.value;
      setTextValue(textAreaValue);
      updateCellState?.(shouldTextFieldExpand(ref, initialTextAreaHeight.current) ? 'Expanded' : 'CellSecondSelect');
    }
  };

  return {
    handleBlur: onBlur,
    handleKeyDown: onKeyDown,
    handleTextChanged,
    textAreaRef,
    textValue,
  };
};
